import LineString from 'ol/geom/LineString';
import SourceVector from 'ol/source/Vector';
import Style from 'ol/style/Style';
import { Feature } from 'ol';
import { fromLonLat } from 'ol/proj';
import { Stroke } from 'ol/style';

import { authGetRequest } from 'helpers/apiRequests';
import { clearMapLayer } from 'utils/map/layers';
import { hasArrayJustOneElement } from 'utils/main';
import { TRACK_LINE_STYLE, UAVO_HISTORY_PERIOD } from 'consts/mapConfig';

import { getFeatureByIdFromLayer } from './common';

const trackUavo = (selectedUavos, trackerLayer) => {
  if (!hasArrayJustOneElement(selectedUavos)) {
    doNotTrackUavo(trackerLayer);
    return;
  }

  const uavo = selectedUavos[0];
  const trackUavoFeatureId = 'trackFeature';
  const trackUavoFeature = getFeatureByIdFromLayer(
    trackerLayer,
    trackUavoFeatureId
  );
  const uavoPosition = uavo.getGeometry().getCoordinates();
  const uavoData = uavo.getProperties().data;

  if (shouldAppendCoordToCurrentTrackFeature(trackUavoFeature, uavo)) {
    if (isPositionNew(trackUavoFeature, uavoPosition)) {
      appendCoordinateToFeature(trackUavoFeature, uavoPosition);
      trimCoordinatesIfExceedsLimit(trackUavoFeature, 2000);
    }
  } else {
    addNewTrackUavoFeatureToLayer(trackerLayer, {
      trackUavoFeatureId,
      uavoPosition,
      uavoData,
    });
  }
};

const doNotTrackUavo = trackLayer => {
  clearMapLayer(trackLayer);
};

const shouldAppendCoordToCurrentTrackFeature = (
  currentTrackUavoFeature,
  uavo
) => {
  const id = uavo.getProperties().data.id
    ? uavo.getProperties().data.id
    : uavo.getProperties().data.uid;
  return !!(
    currentTrackUavoFeature &&
    currentTrackUavoFeature.getProperties().uavoId === id
  );
};

const isPositionNew = (uavoTrackFeature, currentPosition) => {
  const lastCoord = uavoTrackFeature.getGeometry().getLastCoordinate();

  return !(
    lastCoord[0] === currentPosition[0] && lastCoord[1] === currentPosition[1]
  );
};

const appendCoordinateToFeature = (feature, coordinates) => {
  feature.getGeometry().appendCoordinate(coordinates);
};

const trimCoordinatesIfExceedsLimit = (uavoTrackFeature, maxCoords) => {
  const coordinates = uavoTrackFeature.getGeometry().getCoordinates();
  if (coordinates.length > maxCoords) {
    uavoTrackFeature
      .getGeometry()
      .setCoordinates(coordinates.slice(coordinates.length - maxCoords));
  }
};

const addNewTrackUavoFeatureToLayer = (layer, dataToCreateFeature) => {
  const trackUavoFeature = createNewTrackUavoFeature(dataToCreateFeature);
  const sourceVector = createSourceVectorWithFeature(trackUavoFeature);
  layer.setSource(sourceVector);
};

const createNewTrackUavoFeature = featuresData => {
  const newUavoTrackFeature = new Feature();
  newUavoTrackFeature.setId(featuresData.trackUavoFeatureId);
  newUavoTrackFeature.setProperties({
    uavoId: featuresData.uavoData.id || featuresData.uavoData.uid,
  });
  newUavoTrackFeature.setStyle(
    new Style({
      stroke: new Stroke(TRACK_LINE_STYLE.stroke),
    })
  );
  newUavoTrackFeature.setGeometry(
    new LineString([featuresData.uavoPosition, featuresData.uavoPosition])
  );

  addUavoHistoricPositions(newUavoTrackFeature, featuresData.uavoData);

  return newUavoTrackFeature;
};

const addUavoHistoricPositions = async (uavoTrackFeature, uavo) => {
  const historicPositions = await getUavoHistory(uavo);
  uavoTrackFeature.getGeometry().setCoordinates(historicPositions);
};

const getUavoHistory = async uavo => {
  const { start, stop } = getDates();
  const { uid } = uavo;
  let endPoint;
  if (uid && uid.indexOf('DOS') === -1) {
    endPoint = uavo.type === 'drone' ? 'droneHistory' : 'uavoHistory';
  } else endPoint = 'radarHistory';

  const result = await authGetRequest(endPoint, { uid, start, stop });
  if (result?.status === 200) {
    const positions = result.data.map(uavoPosition =>
      fromLonLat([uavoPosition.x, uavoPosition.y])
    );
    return positions;
  }
  return [];
};

const getDates = () => {
  const start = new Date();
  start.setHours(start.getHours() - UAVO_HISTORY_PERIOD);
  const end = new Date();

  return { start: start.getTime(), stop: end.getTime() };
};

const createSourceVectorWithFeature = feature => {
  const vectorSource = new SourceVector({});
  vectorSource.addFeature(feature);

  return vectorSource;
};

export { trackUavo };
